import React, {ReactNode, useCallback, useEffect, useState} from "react"
import {Drawer, Layout} from "antd"
import {Sidenav} from "./Sidenav"
import {Header} from "./Header"
import {Footer} from "./Footer"
import {HubConnection, HubConnectionBuilder} from "@microsoft/signalr"
import {apiUrl} from "../api/API"
import {WSMessageType} from "../types/WebSocketTypes"
import {getMoreNotifications, getNotifications, getNotViewedNotificationsCount, setMessage} from "../store/appReducer"
import {setWSUser} from "../store/userReducer"
import {setWSUserGroup} from "../store/userGroupReducer"
import {setWSCompany} from "../store/companyReducer"
import {setWSObjectGroup} from "../store/objectGroupReducer"
import {setWSObject} from "../store/objectReducer"
import {setWSPSLeakDevice} from "../store/psLeakDeviceReducer"
import {setWSPSLeakDeviceLog} from "../store/psLeakDeviceLogReducer"
import {setWSPSLeakDeviceLocalLog} from "../store/psLeakDeviceLocalLogReducer"
import {setWSPSLeakDeviceIndication} from "../store/psLeakDeviceIndicationReducer"
import {setWSAttributesVersion} from "../store/attributesVersionReducer"
import {setWSObjectGroupSchedule} from "../store/objectGroupScheduleReducer"
import {setWSObjectSchedule} from "../store/objectScheduleReducer"
import {setWSSecurityLog} from "../store/securityLogReducer"
import {setWSUnregisterPSLeakDevice} from "../store/unregisterPsLeakDeviceReducer"
import {setWSUinList} from "../store/uinListReducer"
import {UserNotifyState, UserResponse} from "../models/response"

const {Header: AntHeader, Content, Sider} = Layout

const getWithTimer = (func: any) => setTimeout(() => func(), 500)

type PropsTypes = {
    profile: UserResponse | null
    dispatch: any
    controller: AbortController
    children: ReactNode
}

let connection: HubConnection | null = null

export const Main: React.FC<PropsTypes> = ({profile, dispatch, controller, children}) => {
    const [visible, setVisible] = useState(false)
    const openDrawer = () => setVisible(!visible)

    const connect = useCallback(async () => {
        if (profile) {
            try {
                await connection?.off("ReceiveMessage")
                await connection?.stop()

                connection = new HubConnectionBuilder()
                    .withUrl(`${apiUrl}/webSocket?idUserType=${profile.idUserType.toString()}&idCompany=${profile.idCompany.toString()}`)
                    .withAutomaticReconnect()
                    .build()

                await connection.start()
                connection.on('ReceiveMessage', (message: WSMessageType) => {
                    console.log('message', message)
                    switch (message.type) {
                        case 'popupMessage':
                            if (profile && profile.notify !== UserNotifyState.Disable) {
                                getWithTimer(() => dispatch(setMessage({
                                    type: message.actionType,
                                    message: message.action,
                                    sound: profile.notify === UserNotifyState.EnableWithSound
                                })))
                            }
                            break
                        case 'notification':
                            getWithTimer(() => dispatch(getMoreNotifications({limit: 20, offset: 0, controller, withoutLoadingIndicator: true})))
                            break
                        case 'userGroup':
                            getWithTimer(() => dispatch(setWSUserGroup(message)))
                            break
                        case 'user':
                            getWithTimer(() => dispatch(setWSUser(message)))
                            break
                        case 'company':
                            getWithTimer(() => dispatch(setWSCompany(message)))
                            break
                        case 'objectGroup':
                            getWithTimer(() => dispatch(setWSObjectGroup(message)))
                            break
                        case 'object':
                            getWithTimer(() => dispatch(setWSObject(message)))
                            break
                        case 'psLeakDevice':
                            getWithTimer(() => dispatch(setWSPSLeakDevice(message)))
                            break
                        case 'unregisterPsLeakDevice':
                            getWithTimer(() => dispatch(setWSUnregisterPSLeakDevice(message)))
                            break
                        case 'psLeakDeviceLog':
                            getWithTimer(() => dispatch(setWSPSLeakDeviceLog(message)))
                            break
                        case 'psLeakDeviceLocalLog':
                            getWithTimer(() => dispatch(setWSPSLeakDeviceLocalLog(message)))
                            break
                        case 'psLeakDeviceIndication':
                            getWithTimer(() => dispatch(setWSPSLeakDeviceIndication(message)))
                            break
                        case 'attributesVersion':
                            getWithTimer(() => dispatch(setWSAttributesVersion(message)))
                            break
                        case 'objectGroupSchedule':
                            getWithTimer(() => dispatch(setWSObjectGroupSchedule(message)))
                            break
                        case 'objectSchedule':
                            getWithTimer(() => dispatch(setWSObjectSchedule(message)))
                            break
                        case 'securityLog':
                            getWithTimer(() => dispatch(setWSSecurityLog(message)))
                            break
                        case 'psLeakDeviceUin':
                            getWithTimer(() => dispatch(setWSUinList(message)))
                            break
                    }
                })
            } catch (e: any) {
                console.log('Connection failed: ', e)
            }
        }
    }, [dispatch, controller, profile])

    useEffect(() => {
        connect()
    }, [connect])

    useEffect(() => {
        return () => {
            connection?.off("ReceiveMessage")
            connection?.stop()
        }
    }, [])

    return (
        <Layout className="layout-dashboard">
            <Drawer title={false}
                    placement="left"
                    closable={false}
                    onClose={() => setVisible(false)}
                    open={visible}
                    key="left"
                    width={270}
                    className="drawer-sidebar"
            >
                <Layout className="layout-dashboard"
                >
                    <Sider trigger={null}
                           width={280}
                           theme="light"
                           className="sider-primary ant-layout-sider-primary"
                           style={{background: 'transparent'}}
                    >
                        <Sidenav profile={profile}/>
                    </Sider>
                </Layout>
            </Drawer>
            <Sider breakpoint="lg"
                   collapsedWidth="0"
                   onCollapse={(collapsed, type) => {
                       // console.log(collapsed, type);
                   }}
                   trigger={null}
                   width={280}
                   theme="light"
                   className="sider-primary ant-layout-sider-primary"
                   style={{background: 'transparent'}}
            >
                <Sidenav profile={profile}/>
            </Sider>
            <Layout>
                <AntHeader>
                    <Header onPress={openDrawer}/>
                </AntHeader>
                <Content className="content-ant">{children}</Content>
                <Footer />
            </Layout>
        </Layout>
    )
}
